/**
 * accountPickerViewModel — `(state)` → les groupes de « Qui utilise Masteur ? ».
 *
 * Patron C (cache seul) : les comptes gardés sur l'appareil (`authTokens.storedTokens`) et les
 * familles connues (`data.family`), déjà dans le store. Aucune lecture réseau.
 *
 * Port littéral de `FamilyAccountSelectionScreen` (legacy) :
 *  - le rangement est celui de `groupAccountsByFamily(storedTokens, familyState, true)` : les
 *    familles d'au moins deux comptes, dans l'ordre où il les rend, puis les « Comptes individuels »
 *    (id `-1`) — une famille d'un seul compte y passe ;
 *  - dans un groupe, les élèves puis les autres comptes, chacun trié du plus récemment utilisé au
 *    plus ancien (`FamilyGroupSection` : `new Date(b.lastConnected || 0) - new Date(a.lastConnected || 0)`) ;
 *  - « Espace parent » s'ouvre par le PREMIER élève du groupe dans l'ordre de l'appareil
 *    (`handleParentModePress` : `familyGroup.accounts.find(...)`), pas par le plus récent :
 *    `parentSpaceAccountId` le porte.
 * Le nom d'une famille ne s'écrit qu'à plusieurs groupes : c'est la page qui le décide (`alone`).
 *
 * Mémoïsé sur les deux tranches lues (`ports/CLAUDE.md` § view-models).
 */
import { createSelector } from "reselect"

import { hasRole } from "../../../../../data/utility/ability"
import { groupAccountsByFamily } from "../../../../../data/utility/familyUtils"
import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"
import { type ApiStoredAccount, toUIStoredAccounts } from "../../../adapters/auth/AccountPicker/storedAccountAdapter"
import { type UIStoredAccountGroup } from "../../../ui/types"

export type AccountPickerViewModel = {
    groups: UIStoredAccountGroup[]
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

/** Ce que `groupAccountsByFamily` rend pour un groupe (forme lâche, JS). */
type ApiFamilyGroup = {
    familyId?: string | number
    familyName?: string
    accounts?: ApiStoredAccount[]
    isIndividualGroup?: boolean
}

const isStudent = (account: ApiStoredAccount) => hasRole(account, "student") === true

const lastConnectedTime = (account: ApiStoredAccount) => new Date(account.lastConnected || 0).getTime()

/** Du plus récemment utilisé au plus ancien ; le tri est stable, l'ordre de l'appareil départage. */
const byMostRecent = (accounts: ApiStoredAccount[]) =>
    [...accounts].sort((a, b) => lastConnectedTime(b) - lastConnectedTime(a))

export function toUIStoredAccountGroup(group: ApiFamilyGroup): UIStoredAccountGroup | null {
    const accounts = (group.accounts ?? []).filter((account) => account != null)
    const students = toUIStoredAccounts(byMostRecent(accounts.filter(isStudent)))
    const others = toUIStoredAccounts(byMostRecent(accounts.filter((account) => !isStudent(account))))
    if (students.length + others.length === 0) return null

    // « Espace parent » s'ouvre par ce premier élève : pas de tuile quand son espace a éteint le bloc Parents.
    const firstStudent = accounts.find((account) => isStudent(account) && account.userId != null)
    const firstStudentId = isBlockOn(firstStudent, TENANT_BLOCKS.PARENTS) ? firstStudent?.userId : undefined
    const lists = {
        students,
        others,
        ...(firstStudentId != null ? { parentSpaceAccountId: firstStudentId } : {}),
    }
    if (group.isIndividualGroup) return { kind: "individual", id: -1, ...lists }
    return { kind: "family", id: group.familyId ?? -1, name: group.familyName ?? "", ...lists }
}

const selectStoredTokens = (state: ReduxState) => state?.data?.authTokens?.storedTokens
const selectFamily = (state: ReduxState) => state?.data?.family

export const accountPickerViewModel = createSelector(
    [selectStoredTokens, selectFamily],
    (storedTokens, family): AccountPickerViewModel => {
        const { familyGroups } = groupAccountsByFamily(storedTokens, family, true) as {
            familyGroups: ApiFamilyGroup[]
        }
        return {
            groups: (familyGroups ?? [])
                .map(toUIStoredAccountGroup)
                .filter((group): group is UIStoredAccountGroup => group != null),
        }
    }
)
